import React, { ReactNode, useEffect, useMemo, useContext, useCallback, useState, ReactElement } from 'react';
import { AxiosRequestConfig, AxiosResponse } from 'axios';

import { AugmentedStore } from 'ia-react-core/~/interfaces/AugmentedStore';
import resolveScope from 'ia-react-core/~/utils/redux/_resolveScope';
import getDataDst from 'ia-react-core/~/utils/components/_getDataDst';
import { useDispatch } from 'react-redux';
import updateResourceById from 'ia-react-core/~/actions/resource-fn/updateResourceById';
import throwError from '../../../utils/error/_throwError';
import Saga from '../../redux/Saga';
import ResourceGroupContext from '../_ResourceGroupContext';
import LoadBufferContext from '../../loader/_LoadBufferContext';

import resourceGroupSaga from './ResourceGroup.saga';
import { ResourceProps } from '../Resource/Resource';

export interface ResourceGroupDataItem { name: string; data: Object }

export type ResourceGroupSuccesses<DataList extends ResourceGroupDataItem[]> = {
  [P in keyof DataList]: {
    resourceId: number;
    name: DataList[P] extends ResourceGroupDataItem ? DataList[P]['name'] : never;
    request: DataList[P] extends ResourceGroupDataItem ? AxiosResponse<DataList[P]['data']> : never;
  }
};

export type ResourceGroupResponseData<DataList extends Iterable<ResourceGroupDataItem>> = {
  [
    P in keyof DataList as Exclude<P, string | symbol | number> extends P
      ? DataList[P] extends ResourceGroupDataItem
        ? DataList[P]['name']
        : never
      : never
  ]: P extends number ? never : DataList[P] extends ResourceGroupDataItem ? DataList[P]['data'] : never;
};

export interface GroupedResourceInfo {
  resourceId: number;
  dataDst?: string | string[];
  autoRequestIfEmpty?: boolean;
}

export interface ResourceGroupProps<DataList extends ResourceGroupDataItem[], Error, StateData> {
  children?: ReactNode;
  name: string;
  dataDst?: string | (() => string);
  autoRequest?: boolean;
  autoRequestIfEmpty?: boolean;
  runInSequence?: boolean;
  onRequest?: (config: AxiosRequestConfig) => void;
  onSuccess?: (successes: ResourceGroupSuccesses<DataList>) => void;
  onFailure?: (error: Error) => void;
  onComplete?: () => void;
  onCancel?: () => void;
  transformResponsesData?: (data: ResourceGroupResponseData<DataList>) => StateData;
  responseResolvers?: ((successes: ResourceGroupSuccesses<DataList>, options: { store: AugmentedStore<any>; scope: string[]}) => Promise<unknown>)[];
}

interface ResourceGroupInternalProps {
  resourceId: number;
  resourceReducerKey: string;
  scope: string[];
  groupedResourceInfos?: GroupedResourceInfo[];
  store: AugmentedStore<{}>;
}

export interface ResourceGroupComposedProps<DataList extends ResourceGroupDataItem[], Error, StateData> extends ResourceGroupProps<DataList, Error, StateData>, ResourceGroupInternalProps {}

const ResourceGroupDefaultProps = {
  autoRequest: false,
  autoRequestIfEmpty: false,
  runInSequence: false,
};

const ResourceGroup = <DataList extends ResourceGroupDataItem[], Error, StateData>({
  children, resourceId, name, dataDst, runInSequence, autoRequest, autoRequestIfEmpty, store, onSuccess, scope, onFailure, onCancel, onComplete, resourceReducerKey, responseResolvers,
}: ResourceGroupComposedProps<DataList, Error, StateData>) => {
  const dispatch = useDispatch();

  const [groupedResourceInfos, setGroupedResourceInfos] = useState<GroupedResourceInfo[]>([]);

  const resolvedDataDst = useMemo<string | string[]>(() => {
    const currentDataDst = getDataDst({ dataDst });
    const asbDataDst = currentDataDst && `/${resolveScope(currentDataDst, scope)}`;
    if (asbDataDst) return asbDataDst;
    return groupedResourceInfos.map((groupedResourceInfo) => groupedResourceInfo.dataDst) as string[];
  }, [dataDst, groupedResourceInfos, scope]);

  const resourceList = useMemo(() => {
    if (!React.Children.count(children)) {
      throwError('ResourceGroup: Expecting at least one "Resource" as children.');
    }

    return React.Children.toArray(children)
      .filter((child) => child)
      .map((child) => {
        const newProps = {
          autoRequest: false,
          effect: 'latest',
          isGrouped: true,
          dataDst: (child as ReactElement)?.props?.dataDst || ((p: ResourceProps<any>) => `${resolvedDataDst as string}.${p.name}`),
        };

        return React.cloneElement(child as ReactElement, newProps);
      });
  }, [children, resolvedDataDst]);

  const { registerLoader, unregisterLoader } = useContext(LoadBufferContext);

  const {
    registerResource: registerResourceOnParent,
    unregisterResource: unregisterResourceOnParent,
  } = useContext(ResourceGroupContext) || {};

  useEffect(() => {
    if (registerResourceOnParent) {
      registerResourceOnParent({ resourceId, dataDst: resolvedDataDst, autoRequestIfEmpty });
      return () => {
        unregisterResourceOnParent({ resourceId });
      };
    } if (registerLoader && (autoRequest || autoRequestIfEmpty)) {
      registerLoader({ resourceId, name });
      return () => {
        unregisterLoader({ resourceId, name });
      };
    }
    return undefined;
  }, [autoRequest, autoRequestIfEmpty, dataDst, name, registerLoader, registerResourceOnParent, resolvedDataDst, resourceId, unregisterLoader, unregisterResourceOnParent]);

  const registerResource = useCallback(({ resourceId: groupedResourceId, dataDst: groupedDataDst }: GroupedResourceInfo) => {
    setGroupedResourceInfos((currentGroupedResourceInfos) => {
      if (!currentGroupedResourceInfos.find((currentGroupedResourceInfo) => currentGroupedResourceInfo.resourceId === groupedResourceId)) {
        return [...currentGroupedResourceInfos, { resourceId: groupedResourceId, dataDst: groupedDataDst }];
      }
      return currentGroupedResourceInfos;
    });
  }, []);

  const unregisterResource = useCallback(({ resourceId: groupedResourceId }: GroupedResourceInfo) => {
    setGroupedResourceInfos((currentGroupedResourceInfos) => {
      const index = currentGroupedResourceInfos.findIndex((currentGroupedResourceInfo) => currentGroupedResourceInfo.resourceId === groupedResourceId);
      if (index !== -1) {
        return [...currentGroupedResourceInfos.slice(0, index), ...currentGroupedResourceInfos.slice(index + 1, currentGroupedResourceInfos.length)];
      }
      return currentGroupedResourceInfos;
    });
  }, []);

  const resourceGroupContext = useMemo(() => ({
    registerResource,
    unregisterResource,
    parentResourceKey: resourceReducerKey,
  }), [registerResource, resourceReducerKey, unregisterResource]);

  useEffect(() => {
    dispatch(updateResourceById(resourceId, { dataDst: resolvedDataDst as string }));
  }, [dispatch, resolvedDataDst, resourceId]);

  return (
    <ResourceGroupContext.Provider
      value={resourceGroupContext}
    >
      {Boolean(groupedResourceInfos.length) && (
        <Saga
          resourceId={resourceId}
          name={name}
          saga={resourceGroupSaga}
          groupedResourceInfos={groupedResourceInfos}
          runInSequence={runInSequence}
          scope={scope}
          store={store}
          onSuccess={onSuccess}
          onFailure={onFailure}
          onCancel={onCancel}
          onComplete={onComplete}
          autoRequest={autoRequest}
          resolvedDataDst={resolvedDataDst}
          autoRequestIfEmpty={autoRequestIfEmpty}
          responseResolvers={responseResolvers}
        />
      )}
      {resourceList}
    </ResourceGroupContext.Provider>
  );
};

ResourceGroup.type = Symbol('ResourceGroup');
ResourceGroup.defaultProps = ResourceGroupDefaultProps;

export default ResourceGroup;
